import { useCallback, useRef, useState } from "react";

/**
 * Watches a container element with ResizeObserver and computes the scale
 * factor needed to fit a `contentWidth` x `contentHeight` box inside it.
 *
 * Both axes are considered (not just width) so tall shapes — a portrait
 * tablet is a good example, e.g. 1024x1366 — don't scale purely off their
 * width and end up taller than the workspace, which is what previously
 * made tablet cards blow past their row and crowd neighboring cards.
 * `maxHeight` is an additional display cap (independent of the container's
 * own height, which isn't meaningfully constrained by the flex layout).
 *
 * When `zoom` is a number (e.g. 0.5) it takes priority over the fitted
 * scale — the container still determines available space, but the caller
 * has pinned an explicit zoom level.
 */
export function useResponsiveScale(contentWidth, contentHeight, zoom, maxHeight) {
  const [containerWidth, setContainerWidth] = useState(0);
  const observerRef = useRef(null);

  // A callback ref (not useEffect) so the container's real width is read
  // synchronously the moment it's attached to the DOM, during React's
  // commit phase, before the browser paints. Waiting for ResizeObserver's
  // first async callback instead meant the very first paint used a
  // fallback "fit width" of 1 (unconstrained), then snapped down to the
  // real, usually much smaller, scale a frame later — a visible pop/jump
  // every time a card appeared. Measuring here removes that flash.
  const containerRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) return;

    setContainerWidth(node.getBoundingClientRect().width);

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  const widthFit = containerWidth > 0 && contentWidth > 0 ? containerWidth / contentWidth : 1;
  const heightFit = maxHeight > 0 && contentHeight > 0 ? maxHeight / contentHeight : 1;
  const fittedScale = Math.min(widthFit, heightFit, 1);

  const scale = zoom === "fit" || zoom == null ? fittedScale : Number(zoom);

  return { containerRef, scale: Number.isFinite(scale) && scale > 0 ? scale : 1, containerWidth };
}
